import { Cross2Icon } from "@radix-ui/react-icons"
import { useEffect, useRef, useState } from "react"
import { useDebounceValue } from "usehooks-ts"
import { Input } from "@/components/ui/input"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import PlusLineIcon from "@/components/icons/plus-line"

interface SearchProps {
  selectedResult?: string;
  onSelectResult: (value?: string | null) => void;
  onSearch: (query: string) => void;
  isSearching: boolean;
  renderItem: (value: string) => React.ReactNode;
  placeholder?: string;
  results: string[];
  onAddItem?: () => void;
  addItemLabel?: string;
}

export function SearchSelect({
  selectedResult,
  onSelectResult,
  onSearch,
  isSearching,
  renderItem,
  placeholder,
  results,
  onAddItem,
  addItemLabel,
}: SearchProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearchQuery] = useDebounceValue(searchQuery, 500)
  const [selectOpen, setSelectOpen] = useState(false)
  const onSearchRef = useRef(onSearch)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    onSearchRef.current = onSearch
  }, [onSearch])

  useEffect(() => {
    if (debouncedSearchQuery === "") return
    onSearchRef.current(debouncedSearchQuery)
    setSelectOpen(true)
  }, [debouncedSearchQuery])

  const onSelectItem = (value: string) => {
    onSelectResult(value)
    setSearchQuery("")
  }

  const onClear = () => {
    setSearchQuery("")
    onSelectResult(null)
  }

  if (selectedResult) {
    return (
      <Card className="shadow-sm rounded-md h-10 p-1">
        <CardContent className="flex-grow flex flex-row p-0 pl-3 pr-0 justify-between items-center">
          {renderItem(selectedResult)}
          <Button
            onClick={onClear}
            variant="ghost"
            size="sm"
            className="text-gray-500"
            type="button"
          >
            <Cross2Icon />
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={placeholder}
        type="text"
        role="searchbox"
        className="h-10 shadow-sm"
        onFocus={() => setSelectOpen(true)}
        onBlur={() => setTimeout(() => setSelectOpen(false), 200)}
      />
      {selectOpen && (
        <div className="absolute z-10 shadow-md mt-2 rounded-md border p-0 flex flex-col gap-0 bg-white overflow-hidden w-full">
          {/* TODO: these should have proper loading aria */}
          {isSearching && (
            <div className="text-gray-500 font-normal text-sm">
              Searching...
            </div>
          )}
          {!isSearching && searchQuery && !results.length && (
            <div className="text-gray-500 font-normal text-sm px-5 py-3">
              Nothing found for "{debouncedSearchQuery}"
            </div>
          )}
          {results.map((value) => {
            return (
              <Button
                type="button"
                variant="ghost"
                className="justify-start text-black rounded-sm"
                key={value}
                onClick={() => onSelectItem(value)}
                data-testid="search-select-item"
              >
                {renderItem(value)}
              </Button>
            )
          })}
          {!isSearching && onAddItem && (
            <Button
              type="button"
              variant="ghost"
              className="justify-start w-full rounded-sm font-normal"
              onClick={onAddItem}
              data-testid="search-select-add-item"
            >
              <PlusLineIcon /> {addItemLabel || "Add item"}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
